import { useReducer, useEffect } from "react";
import { useDispatch, useSelector } from 'react-redux';
import { contextReducerLogging } from 'config';
import { useNavigate } from 'react-router-dom';
import { Set } from 'immutable';
import { DateTime } from "luxon";
import { Assignments } from "actions/sharedJsActions";

const { assignmentsCreate, assignmentsUpdate } = Assignments;

export const initialState = (assignment, examTemplate, questions, subjects, users, memberGroups) => ({
  id: assignment?.get('id') || null,
  name: assignment?.get('name') || '',
  templateId: assignment?.get('template_id') || examTemplate?.get('id'),
  questionBankId: assignment?.get('question_bank_id') || examTemplate?.get('question_bank_id'),
  organizationId: assignment?.get('organization_id'),
  boardExamInterface: assignment?.get('board_exam_interface') || '',
  quizType: assignment?.get('quiz_type'),
  timed: assignment?.get('is_adaptive') ? false : assignment?.get('timed'),
  secondsPerQuestion: assignment?.get('seconds_per_question') || 0,
  noPause: assignment?.get('no_pause'),
  message: assignment?.get('initial_message') || '',
  startTime: assignment?.get('start_time') ? DateTime.fromMillis(assignment.get('start_time')) : null,
  endTime: assignment?.get('end_time') ? DateTime.fromMillis(assignment.get('end_time')) : null,
  started: assignment?.get('started') || false,
  scheduled: (assignment?.get('started') || (assignment?.get('start_time') ? (DateTime.fromMillis(assignment.get('start_time')) < DateTime.now().plus({ minutes: 30 })) : false)),
  randomizeAnswers: assignment?.get('randomize_answers'),
  randomizeQuestions: assignment?.get('randomize_questions') || false,
  hideReview: assignment?.get('hide_review') ? DateTime.fromISO(assignment?.get('hide_review')) : null,
  hideResults: assignment?.get('hide_results') ? DateTime.fromISO(assignment?.get('hide_results')) : null,
  isAdaptive: assignment?.get('isAdaptive'),
  timeMode: 'perQuestion',
  maxTime: 14400,
  totalTime: 600,
  submitted: false,
  groupIds: assignment?.get('member_group_ids').map(id => id.toString()) || [],
  users: assignment && users
    ? users.filter(u => assignment.get('user_ids').toArray().includes(u.get('id'))).toList().toArray().map(u => (
      { [(u.get('id'))]: { id: (u.get('id')), name: `${u.get('first_name')} ${u.get('last_name')}`, email: u.get('email') } }
    ))
      .reduce((obj, user) => Object.assign(obj, user), {})
    : {},
  memberGroups: assignment && memberGroups
    ? memberGroups.filter(mg => assignment.get('member_group_ids').toArray().includes(mg.get('id'))).toList().toArray().map(mg => (
      { [mg.get('id')]: { id: (mg.get('id')), name: mg.get('name'), total_members: mg.get('member_ids').toArray().length } }
    ))
      .reduce((obj, group) => Object.assign(obj, group), {})
    : {},
  results: [],
  accommodations: assignment?.get('accommodations') && users
    ? users.filter(u => Object.keys(assignment?.get('accommodations')).includes(u.get('id').toString())).toList().toArray().map(u => (
      {
        [(u.get('id'))]: {
          first_name: u.get('first_name'), last_name: u.get('last_name'), email: u.get('email'), ...assignment?.get('accommodations')[u.get('id')]
        }
      }
    ))
      .reduce((obj, user) => Object.assign(obj, user), {})
    : {},
  delegates: assignment && users
    ? users.filter(u => assignment.get('delegate_ids').toArray().includes(u.get('id'))).toList().toArray().map(u => (
      { [(u.get('id'))]: { id: (u.get('id')), name: `${u.get('first_name')} ${u.get('last_name')}`, email: u.get('email') } }
    ))
      .reduce((obj, user) => Object.assign(obj, user), {})
    : {},
  questions: questions,
  selectedSubjects: [],
  questionIds: assignment?.get('results'),
  allSubjects: assignment && subjects
    ? !subjects.find((subject, subjectId) => !(assignment?.get('selectedSubjects')?.toArray() || []).includes(subjectId))
    : true,
  questionsBySubject: {},
  errors: new Set(),
  isNgnOn: false,
  nextGenSelected: ['', 'nextGen'].includes(examTemplate?.get('question_type'))
});

export const requestFormatter = ({
  name,
  templateId,
  questionBankId,
  boardExamInterface,
  quizType,
  timed,
  secondsPerQuestion,
  noPause,
  startTime,
  endTime,
  randomizeQuestions,
  randomizeAnswers,
  isAdaptive,
  accommodations,
  memberGroups,
  delegates,
  message,
  users,
  selectedSubjects,
  results,
  hideResults,
  hideReview
}) =>
  ({
    name,
    exam_template_id: templateId,
    question_bank_id: questionBankId,
    board_exam_interface: boardExamInterface === '' ? null : boardExamInterface,
    quiz_type: quizType,
    timed,
    seconds_per_question: secondsPerQuestion,
    no_pause: noPause,
    start_time: startTime || new Date(),
    end_time: endTime,
    cutoff: true,
    randomize_questions: randomizeQuestions,
    randomize_answers: randomizeAnswers,
    is_adaptive: isAdaptive,
    // eslint-disable-next-line guard-for-in, no-restricted-syntax
    accommodations: (!!endTime || timed) ? (() => { for (const key in accommodations) { delete accommodations[key].updated_at; } return accommodations; })() : {},
    group_ids: Object.keys(memberGroups),
    delegate_ids: Object.keys(delegates),
    message: message,
    adaptive_eligible_subject_ids: selectedSubjects,
    user_emails: Object.values(users).map(user => user.email),
    hide_results: hideResults || null,
    hide_review: hideReview || null
  });

const reducer = (state, action) => {
  const { type, payload } = action;
  if (contextReducerLogging) {
    console.log('%c%s',
      'color: green; background: yellow;', type, payload);
  }

  const newState = (() => {
    switch (type) {
      case "INITIALIZE":
        return {
          ...initialState(payload.assignment)
        };
      case "CHANGE_NAME":
        return { ...state, name: payload.name };
      case "CHANGE_BOARD_EXAM_INTERFACE":
        return { ...state, boardExamInterface: payload.boardExamInterface };
      case "CHANGE_QUIZ_TYPE":
        return { ...state, quizType: payload.quizType, timed: payload.quizType === 'adaptive' ? false : state.timed };
      case "CHANGE_TIMED":
        return { ...state, timed: payload.timed };
      case "CHANGE_TIME_MODE":
        return { ...state, timeMode: payload.timeMode };
      case "CHANGE_QUESTION_BANK":
        return { ...state, questionBankId: payload.questionBankId, isNgnOn: payload.isNgnOn };
      case "CHANGE_SECONDS_PER_QUESTION":
        return { ...state, secondsPerQuestion: payload.secondsPerQuestion };
      case "CHANGE_TOTAL_TIME":
        return { ...state, totalTime: payload.totalTime };
      case "SET_NO_PAUSE":
        return { ...state, noPause: payload.noPause };
      case "CHANGE_MESSAGE":
        return { ...state, message: payload.message };
      case "SET_TEMPLATE_AND_BANK":
        return { ...state, templateId: payload.templateId, questionBankId: payload.questionBankId };
      case "CHANGE_ACCOMMODATIONS":
        return { ...state, accommodations: payload.accommodations };
      case "CHANGE_USERS":
        return { ...state, users: payload.users };
      case "CHANGE_GROUPS":
        return { ...state, memberGroups: payload.memberGroups };
      case "CHANGE_DELEGATES":
        return { ...state, delegates: payload.delegates };
      case "SET_RANDOMIZE_QUESTIONS":
        return { ...state, randomizeQuestions: payload.randomizeQuestions };
      case "SET_RANDOMIZE_ANSWERS":
        return { ...state, randomizeAnswers: payload.randomizeAnswers };
      case "SET_START_TIME":
        return { ...state, startTime: payload.startTime };
      case "SET_END_TIME":
        return { ...state, endTime: payload.endTime || null };
      case "SET_IS_ADAPTIVE":
        return { ...state, isAdaptive: payload.isAdaptive };
      case "SET_SELECTED_SUBJECTS":
        return { ...state, allSubjects: false, selectedSubjects: [...payload.selectedSubjects] };
      case "CLEAR_ALL_SUBJECTS":
        return { ...state, allSubjects: false, selectedSubjects: [] };
      case "SET_ALL_SUBJECTS":
        return { ...state, allSubjects: true };
      case "SET_HIDE_RESULTS":
        return { ...state, hideReview: null, hideResults: payload };
      case "SET_HIDE_REVIEW":
        return { ...state, hideReview: payload, hideResults: null };
      case "CLEAR_HIDE_RESULTS_AND_HIDE_REVIEW":
        return { ...state, hideResults: null, hideReview: null };
      case "CHANGE_QUESTIONS":
        return { ...state, questions: payload.questions, initialized: payload.questions.length !== 0 };
      case "CLEAR_QUESTIONS":
        return { ...state, selectedQuestionIds: new Set() };
      case "CLEAR_ALL_FILTERS":
        return {
          ...state, selectedSubjects: [], allSubjects: true, boardExamInterface: ''
        };
      case "ADD_ERROR":
        return { ...state, errors: state.errors.add(payload.errors) };
      case "REMOVE_ERROR":
        return { ...state, errors: state.errors.remove(payload.errors) };
      case "SUBMIT_FORM":
        return { ...state, submitted: true };
      default:
        return state;
    }
  })();

  const recountQuestions = () => {
    newState.questionsBySubject = {};
    newState.results = [];
    // Loop through questions a single time to calculate the counts for each input and the final question results
    newState.questions.forEach((question, questionId) => {
      if (question.get('ngn_type') && !newState.isNgnOn) return;
      if (!!question.get('group_id') && !question.get('ngn_type') && newState.isAdaptive) return;

      let hasSelectedSubject = false;
      question.get('subjectIds').forEach((subjectId) => {
        if (!newState.questionsBySubject[subjectId]) newState.questionsBySubject[subjectId] = [];
        newState.questionsBySubject[subjectId].push(parseInt(questionId));
        if (newState.selectedSubjects.includes(subjectId)) hasSelectedSubject = true;
      });
      if (!hasSelectedSubject) return;

      newState.results.push(questionId);
    });
  };

  recountQuestions();

  let needsRecount = false;
  if (newState.allSubjects) {
    // Auto-select all subjects with questions if allSubjects is true
    newState.selectedSubjects = Object.keys(newState.questionsBySubject).map(i => parseInt(i));
    needsRecount = true;
  } else if (!newState.selectedSubjects.length) {
    // Change allSubjects to false if all subjects have been deselected.
    newState.allSubjects = false;
  } else {
    // Deselect subjects without any questions
    newState.selectedSubjects.forEach((subjectId) => {
      if (!newState.questionsBySubject[subjectId]) {
        newState.selectedSubjects = newState.selectedSubjects.filter(sid => sid !== subjectId);
        needsRecount = true;
      }
    });
  }
  if (needsRecount) recountQuestions();

  return newState;
};

export function useAssignmentReducer(assignment, examTemplateId, organizationId) {
  const examTemplate = useSelector(store => store.examTemplates).get(examTemplateId);
  const questionLimit = examTemplate ? examTemplate.get('question_limit') : 0;
  const questions = useSelector(store => store.performanceQuestions);
  const subjects = useSelector(store => store.subjects);
  const users = useSelector(store => store.users);
  const memberGroups = useSelector(store => store.memberGroups);

  const reduxDispatch = useDispatch();
  const navigate = useNavigate();
  const [state, dispatch] = useReducer(reducer, initialState(assignment, examTemplate, questions, subjects, users, memberGroups));
  const {
    id,
    secondsPerQuestion,
    timeMode,
    totalTime,
    submitted
  } = state;

  // // When total time changes adjust time per question accordingly and round down
  useEffect(() => {
    if (timeMode !== 'total') return;
    dispatch({
      type: "CHANGE_SECONDS_PER_QUESTION",
      payload: {
        secondsPerQuestion: !questionLimit ? 0 : Math.ceil(totalTime / questionLimit)
      }
    });
  }, [totalTime]);

  // // When seconds per question changes adjust total time
  useEffect(() => {
    if (timeMode === 'total') return;
    dispatch({
      type: "CHANGE_TOTAL_TIME",
      payload: {
        totalTime: secondsPerQuestion * questionLimit
      }
    });
  }, [secondsPerQuestion]);

  useEffect(() => {
    if (!submitted) return;
    if (!id) {
      const assignmentParams = (requestFormatter({ ...state }));
      reduxDispatch(assignmentsCreate(organizationId, assignmentParams))
        .then((response) => {
          navigate(`/assignments`);
          return response;
        })
        .catch((error) => {
          alert(error);
        });
    } else {
      const assignmentParams = (requestFormatter({ ...state }));
      reduxDispatch(assignmentsUpdate(id, assignmentParams))
        .then((response) => {
          navigate(`/assignments`);
          return response;
        })
        .catch((error) => {
          alert(error);
        });
    }
  }, [submitted]);

  return {
    state,
    dispatch
  };
}
